import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  ICategoryRepository,
  CreateCategoryData,
  UpdateCategoryData,
  GetCategoryData,
  CategoryTreeNode,
} from './interfaces/category.repository.interface';

export class CategoryRepository implements ICategoryRepository {
  private collectionName = 'certificationCategories';

  async getCategories(): Promise<GetCategoryData[]> {
    try {
      const colRef = collection(db, this.collectionName);
      const snapshot = await getDocs(colRef);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as GetCategoryData[];
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CATEGORY_GET_ALL_FAILED',
        devMessage: 'Failed to get categories',
        cause: error,
        level: 'error',
      });
    }
  }

  async getCategoryTree(): Promise<CategoryTreeNode[]> {
    try {
      const colRef = collection(db, this.collectionName);
      const snapshot = await getDocs(query(colRef, orderBy('name', 'asc')));

      const allCategories = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        children: [],
      })) as unknown as CategoryTreeNode[];

      // Build tree structure
      const categoryMap = new Map<string, CategoryTreeNode>();
      const rootCategories: CategoryTreeNode[] = [];

      for (const category of allCategories) {
        categoryMap.set(category.id, category);
        if (!category.parentId) {
          rootCategories.push(category);
        }
      }

      for (const category of allCategories) {
        if (category.parentId) {
          const parent = categoryMap.get(category.parentId);
          if (parent) {
            parent.children.push(category);
          }
        }
      }

      return rootCategories;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CATEGORY_GET_TREE_FAILED',
        devMessage: 'Failed to get category tree',
        cause: error,
        level: 'error',
      });
    }
  }

  async createCategory(categoryData: CreateCategoryData): Promise<string> {
    try {
      const colRef = collection(db, this.collectionName);
      const docRef = await addDoc(colRef, {
        ...categoryData,
        courseCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CATEGORY_CREATE_FAILED',
        devMessage: `Failed to create category "${categoryData.name}"`,
        cause: error,
        level: 'error',
      });
    }
  }

  async updateCategory(categoryId: string, updateData: UpdateCategoryData): Promise<void> {
    try {
      const docRef = doc(db, 'categories', categoryId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CATEGORY_UPDATE_FAILED',
        devMessage: `Failed to update category ${categoryId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteCategory(categoryId: string): Promise<void> {
    try {
      const colRef = collection(db, this.collectionName);

      // Check for child categories
      const childrenSnapshot = await getDocs(
        query(colRef, where('parentId', '==', categoryId)),
      );
      if (!childrenSnapshot.empty) {
        throw new DRXError({
          code: 'CATEGORY_HAS_CHILDREN',
          devMessage: `Cannot delete category ${categoryId} — it has child categories`,
          userMessage: 'Cannot delete category with children',
          level: 'warning',
        });
      }

      // Check for courses in this category
      const coursesSnapshot = await getDocs(
        query(collection(db, 'certificationCourses'), where('categoryId', '==', categoryId)),
      );
      if (!coursesSnapshot.empty) {
        throw new DRXError({
          code: 'CATEGORY_HAS_COURSES',
          devMessage: `Cannot delete category ${categoryId} — it contains courses`,
          userMessage: 'Cannot delete category that contains courses',
          level: 'warning',
        });
      }

      await deleteDoc(doc(db, 'categories', categoryId));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CATEGORY_DELETE_FAILED',
        devMessage: `Failed to delete category ${categoryId}`,
        cause: error,
        level: 'error',
      });
    }
  }
}
